import React, {useState, useEffect} from "react";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsSunburst from "highcharts/modules/sunburst";
import Accordion from 'react-bootstrap/Accordion';
import { chartDefaultData, defaultLevels, graphColors, defaultLevelSettings } from "./defaultData";
import configVars from "../../config";
import { getWithExpiry, setWithExpiry } from '../../common';
import { useCookies } from "react-cookie";
import jwt_decode from "jwt-decode";
import { Link } from 'react-router-dom';

if (typeof Highcharts === "object") {
    HighchartsSunburst(Highcharts);
}

const ChartGenerator = (props) => {

	const [levels, setLevels] = useState( defaultLevels );
	const [graphData, setGraphData] = useState( chartDefaultData );
	const [levelSettings, setLevelSettings] = useState( defaultLevelSettings );
	const [cookies] = useCookies(["sustainyou_user_jwt"]);
	const [saving, setSaving] = useState(false);
	const [chartName, setChartName] = useState('Årshjul');
	const [loading, setLoading] = useState(false);

	useEffect(() => {

		if ( ! props?.chartID ) return;

		if ( props?.chartID === 'new' ) return;

		if ( ! cookies.sustainyou_user_jwt ) {
            return;
        }

		const savedChart = getWithExpiry( `user_pie_chart_${props?.chartID}` );

		if ( savedChart ) {
			console.log( savedChart );
		} else {

			(async () => {

				let userRequestData = new FormData();
				userRequestData.append( 'token', cookies.sustainyou_user_jwt );
				userRequestData.append( 'chart_id', props?.chartID );
	
				setLoading(true);
	
				const fetchUrl = `${configVars.apiUrl}sustainchange/v1/getpiecharts/`;
	
				try{
	
					const response = await fetch(
						fetchUrl,
						{
						method: "POST",
						headers: {
							Authorization: `Basic ${configVars.auth}`,
						},
						body: userRequestData
						}
					);
					
					const updateResponse = await response.json();
			
					if ( updateResponse ) {
	
						if ( updateResponse.success === true ) {
							
							const options = updateResponse?.data?.options;
							const savedLevels  = updateResponse?.data?.levels;

							if ( options && options.series[0].data ) {
								setGraphData( options.series[0].data );
							}

							if ( options && options.series[0].levels ) {
								setLevelSettings( options.series[0].levels );
								//setLevels( options.series[0].levels );
							}

							if ( updateResponse?.data?.title ) {
								setChartName( updateResponse?.data?.title );
							}

							if ( savedLevels && savedLevels.length > 0 ) {
								setLevels( savedLevels );
							}

							//console.log( updateResponse?.data );

						}
	
					}
	
				} catch( error ) {
					console.log( error );
					return error;
				}
	
				setLoading(false);
	
			})()

		}


	}, [cookies.sustainyou_user_jwt, props.chartID]);

	const chartOptions = {
		chart: {
			height: '100%',
			width: '1200'
		},
		colors: graphColors,
		title: {
			text: chartName
		},
		series: [{
			type: 'sunburst',
			data: graphData,
			name: 'Root',
			allowDrillToNode: true,
			borderRadius: 3,
			cursor: 'pointer',
			dataLabels: {
				format: '{point.name}',
				filter: {
					property: 'innerArcLength',
					operator: '>',
					value: 16
				}
			},
			levels: levelSettings,
		}],
		tooltip: false,
	};
	
	if (typeof Highcharts === 'object') {
		HighchartsExporting(Highcharts)
	}

	const handleFieldUpdate = (parentID, childID, name, value) => {

		let updatedField;

		if ( value ) {
			updatedField = { ...levels[parentID].fields[childID], 'name': name, 'value': parseInt( value, 10 ) };
		} else {
			updatedField = { ...levels[parentID].fields[childID], 'name': name };
		}
		
		let newFields = [...levels];
		newFields[parentID].fields[childID] = updatedField;
		setLevels( newFields );

		let newGraphData = [];

		newFields.forEach(group => {
			if ( group.fields && group.fields.length > 0 ) {
				group.fields.forEach(field => {
					newGraphData.push(field);
				});
			}
		});

		// Fixing April missing parent after adding new level.
		let updatedAprilData = { ...newGraphData[4], 'parent': '0' };

		let updatedGraphData = [...newGraphData];
		updatedGraphData[4] = updatedAprilData;

		setGraphData(updatedGraphData );

	}

	const handleNewLevel = () => {
		
		const currentLevels = levels.length;
		const newFields = [];
		const newLevels = levels;
		const lastLevelFields = levels[levels.length-1].fields;

		for (let index = 0; index < lastLevelFields.length; index++) {

			// const id = currentLevels + ( 0.01 * (index+1) );
			// const parent = ( currentLevels - 1 ) + ( 0.01 * (index+1) );
			const name = `Level ${currentLevels+1} column ${index+1}`;

			const id = (parseFloat(lastLevelFields[index].id) + 1).toFixed(3);
			const parent = lastLevelFields[index].id;

			const column = {
				'id': `${id}`,
				'parent': `${parent}`,
				'name': name,
				'value': lastLevelFields[index].value,
			};

			newFields.push( column );
			
		}

		newLevels.push(
			{
				'id': currentLevels,
				'fields': newFields
			}
		);

		//console.log( newLevels );

		setLevels( newLevels );

		let newGraphData = [];

		newLevels.forEach(group => {
			if ( group.fields && group.fields.length > 0 ) {
				group.fields.forEach(field => {
					newGraphData.push(field);
				});
			}
		});

		// Fixing April missing parent after adding new level.
		let updatedAprilData = { ...newGraphData[4], 'parent': '0' };

		let updatedGraphData = [...newGraphData];
		updatedGraphData[4] = updatedAprilData;

		setGraphData(updatedGraphData );

		const newLevelSettings = levelSettings;

		newLevelSettings.push(
			{
				level: currentLevels + 1,
				color: undefined,
				colorVariation: {
					key: 'brightness',
					to: -0.5
				}
			}
		);

		setLevelSettings( newLevelSettings );

	}

	const handleLevelDelete = (levelID) => {

		const newLevels = levels.splice( 0, levelID);
		const newLevelSettings = levelSettings.splice( 0, levelID);
		
		setLevels( newLevels );
		setLevelSettings( newLevelSettings );

		let newGraphData = [];

		newLevels.forEach(group => {
			if ( group.fields && group.fields.length > 0 ) {
				group.fields.forEach(field => {
					newGraphData.push(field);
				});
			}
		});

		setGraphData( newGraphData );
	}

	const handleLevelColor = ( levelID, color) => {

		let updatedColor = { ...levelSettings[levelID], 'color': color };

		let newLevels = [...levelSettings];
		newLevels[levelID] = updatedColor;

		setLevelSettings( newLevels );
	}

	const handleFieldSplit = (parentID, childID, value) => {
		
		let updatedField;

		updatedField = { ...levels[parentID].fields[childID], 'value': parseInt( value/2, 10 ) };

		const newChild = { id: `${(parseFloat(updatedField.id) + 0.001 + ((parentID*childID)+1/100)).toFixed(3)}`, parent: updatedField.parent, name: updatedField.name, value: parseInt( value/2, 10 ), split: 'true'};
		
		let newFields = [...levels];
		newFields[parentID].fields[childID] = updatedField;

		newFields[parentID].fields.splice(childID, 0, newChild );

		//console.log( newFields );

		setLevels( newFields );

		let newGraphData = [];

		newFields.forEach(group => {
			if ( group.fields && group.fields.length > 0 ) {
				group.fields.forEach(field => {
					newGraphData.push(field);
				});
			}
		});

		// Fixing April missing parent after adding new level.
		let updatedAprilData = { ...newGraphData[4], 'parent': '0' };

		let updatedGraphData = [...newGraphData];
		updatedGraphData[4] = updatedAprilData;

		setGraphData( updatedGraphData );

		//console.log( newGraphData );

	}

	const handleFieldDelete = (parentID, childID) => {
		
		const deletedField = levels[parentID].fields[childID];

		const newChildFields = levels[parentID].fields.filter((item) => item.id !== deletedField.id);

		let newFields = [...levels];
		newFields[parentID].fields = newChildFields;

		setLevels( newFields );

		let newGraphData = [];

		newFields.forEach(group => {
			if ( group.fields && group.fields.length > 0 ) {
				group.fields.forEach(field => {
					newGraphData.push(field);
				});
			}
		});

		// Fixing April missing parent after adding new level.
		let updatedAprilData = { ...newGraphData[4], 'parent': '0' };

		let updatedGraphData = [...newGraphData];
		updatedGraphData[4] = updatedAprilData;

		setGraphData( updatedGraphData );

	}

	const handleChartSave = async ( type ) => {

		let userRequestData = new FormData();
            userRequestData.append( 'token', cookies.sustainyou_user_jwt );
			userRequestData.append( 'chart_options', JSON.stringify( chartOptions ) );
			userRequestData.append( 'chart_name', chartName );
			userRequestData.append( 'chart_levels', JSON.stringify( levels ) );

			if ( type === 'update' ) {
				userRequestData.append( 'chart_id', props?.chartID );
			}

            setSaving(true);

            const fetchUrl = `${configVars.apiUrl}sustainchange/v1/savepiecharts/`;

            try{

                const response = await fetch(
                    fetchUrl,
                    {
                    method: "POST",
                    headers: {
                        Authorization: `Basic ${configVars.auth}`,
                    },
                    body: userRequestData
                    }
                );
                
                const updateResponse = await response.json();
        
                if ( updateResponse ) {

                    if ( updateResponse.success === true ) {

						const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
				
						if ( !jwtDecoded.id ) return;
						
						setWithExpiry( `user_pie_charts_${jwtDecoded.id}`, updateResponse.data.user_charts );

						window.location.href = '/piecharts/';

					}

                }

            } catch( error ) {
                console.log( error );
                return error;
            }

            setSaving(false);

	}

    return(
        <div className="generator-data-outer">

			{loading &&
				<div className="spinner-border" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			}

			{!loading &&
				<>
					<div className="generator-data-wrap mb-5">

						{ props?.chartID &&
							<div className="row mb-3">
								<div className="col-12 mb-4">
									<Link to="/piecharts">
										<i className="fa fa-long-arrow-left" aria-hidden="true"></i>
										<span className="ms-2">Tillbaka</span>
									</Link>
								</div>
							</div>
						}

						<div className="row mb-3">
							<div className="col-12 col-lg-3">
								<h3>Chart Name:</h3>
							</div>
							<div className="col-12 col-lg-9">
								<input type="text" className="form-control" value={chartName} onChange={ (e) => setChartName( e.target.value)} />
							</div>
						</div>

						{ levels && levels.length > 0 &&
							<Accordion defaultActiveKey="0">

								{ levels.map( ( level, i ) => {
									return (
										<Accordion.Item eventKey={level.id} key={i}>
											<Accordion.Header>
												<div className="d-flex w-100 align-items-center">
													<span className="flex-grow-1">Level { parseInt( level.id, 10 ) + 1}</span>

													{  parseInt( level.id, 10 ) > 2 &&
														<div className="ps-3">
															<span className="btn btn-secondary btn-sm" onClick={() => handleLevelDelete( level.id )}>
																<i className="fa fa-trash" aria-hidden="true"></i>
																<span className="ms-2">Delete level</span>
															</span>
														</div>
													}

													<div className="px-3 d-flex align-items-center">
														{graphColors.map( (color, k) => {
															return(
																<span className="d-flex" key={k}>
																	{levelSettings[level.id]?.color && levelSettings[level.id].color === color ?
																		<span className="d-flex align-items-center justify-content-center me-1" style={{width:'30px', height:'30px', backgroundColor: color}} onClick={() => handleLevelColor( level.id, undefined)} >
																			<i className="fa fa-check text-white" aria-hidden="true"></i>
																		</span>
																		:
																		<span className="d-inline-block me-1" style={{width:'20px', height:'20px', backgroundColor: color}} onClick={() => handleLevelColor( level.id, color)}></span>
																	}
																	
																</span>
																
															);
														})}
													</div>
													
												</div>
											</Accordion.Header>
											<Accordion.Body>
												{ level.fields.map( (field, j ) => {
													return (
														<div className="row mb-3" key={j}>
															<div className="col-12 col-lg-6">
																<input type="text" value={field.name} className="form-control bg-white" onChange={(e) => { handleFieldUpdate(i, j, e.target.value, field.value ? field.value : '')} } />
															</div>
															{ field.value &&
																<div className="col-12 col-lg-6">
																	<div className="d-flex align-items-center">
																		<div className="flex-grow-1">
																			<input type="number" value={field.value} className="form-control bg-white" min="100" onChange={(e) => { handleFieldUpdate(i, j, field.name, e.target.value > 1 ? e.target.value : 1)} }  />
																		</div>
																		<div className="ps-3">
																			<button type="button" className="btn btn-link p-0 text-secondary text-decoration-none font-size-medium" onClick={() => {handleFieldSplit(i, j, field.value)}}>
																				<i className="fa fa-columns" aria-hidden="true"></i>
																				<span className="ms-2">Split</span>
																			</button>
																		</div>

																		{field.split && field.split === 'true' &&
																			<div className="ps-3">
																				<button type="button" className="btn btn-link p-0 text-secondary text-decoration-none font-size-medium" onClick={ () => handleFieldDelete(i,j)}>
																					<i className="fa fa-trash" aria-hidden="true"></i>
																					<span className="ms-2">Remove</span>
																				</button>
																			</div>
																		}
																	</div>
																</div>
															}
															
														</div>
													);
												})}
											</Accordion.Body>
										</Accordion.Item>
									);
								})}

								
							</Accordion>
						}

						

						{ (! props?.chartID || props?.chartID === 'new') &&
							<div className="d-flex mt-4 justify-content-end">
								<div className="d-flex me-3">
									{chartName && chartName.length > 2 && 
										<button type="button" className="btn btn-halite-blue" onClick={ () => handleChartSave( 'new' )}>
											{saving &&
												<div className="spinner-border spinner-border-sm text-light" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
											}

											{!saving &&
												<i className="fa fa-floppy-o" aria-hidden="true"></i>
											}
											
											<span className="ms-2">Save Chart</span>
										</button>
									}
									
								</div>

								<div className="d-flex">
									<button type="button" className="btn btn-halite-blue" onClick={ () => handleNewLevel()}>
										<i className="fa fa-plus" aria-hidden="true"></i>
										<span className="ms-2">Add new level</span>
									</button>
								</div>
							</div>
						}

						{ props?.chartID && props?.chartID !== 'new' &&
							<div className="d-flex mt-4 justify-content-end">
								<div className="d-flex me-3">
									<button type="button" className="btn btn-halite-blue" onClick={ () => handleNewLevel()}>
										<i className="fa fa-plus" aria-hidden="true"></i>
										<span className="ms-2">Add new level</span>
									</button>
								</div>
								<div className="d-flex">
									<button type="button" className="btn btn-halite-blue" onClick={ () => handleChartSave( 'update' )}>
										{saving &&
											<div className="spinner-border spinner-border-sm text-light" role="status">
												<span className="visually-hidden">Loading...</span>
											</div>
										}
										{!saving &&
											<i className="fa fa-pencil" aria-hidden="true"></i>
										}
										
										<span className="ms-2">Update</span>
									</button>
								</div>
							</div>
						}

						</div>

						<HighchartsReact
							highcharts={Highcharts}
							options={chartOptions}
						/>
				</>
			}

        </div>
    )
}

export default ChartGenerator;
